import { pluralEn, type Count } from '../lib/format'

import type { Translations } from './types'

export const en: Translations = {
  common: {
    all: 'Max',
    back: 'Back',
    cancel: 'Cancel',
    confirm: 'Confirm',
    copy: 'Copy',
    copied: 'Copied',
    open: 'Open',
    retry: 'Try again',
    save: 'Save',
    close: 'Close',
    loading: 'Loading',
    available: 'Available',
    total: 'Total',
    amount: 'Amount',
    network: 'Network',
    asset: 'Asset',
    status: 'Status',
    date: 'Date',
    nothingYet: 'Nothing here yet',
    somethingWentWrong: 'Something went wrong',
    reload: 'Reload',
    crashHint: 'This screen could not be displayed. Reload the app — your money and requests are untouched.',
  },

  nav: {
    label: 'Main navigation',
    home: 'Home',
    markets: 'Markets',
    signals: 'Signals',
    trade: 'Trade',
    wallet: 'Wallet',
  },

  auth: {
    signIn: 'Sign in',
    signInSubtitle: 'Sign in to get back to your signals and balance',
    signInAction: 'Sign in',
    signUp: 'Sign up',
    signUpSubtitle: 'An email and a password are all it takes to start',
    signUpSubtitleInvite: 'An email, a password and an invitation code are all it takes to start',
    email: 'Email',
    emailPlaceholder: 'name@example.com',
    password: 'Password',
    passwordHint: 'At least 8 characters, letters and digits',
    passwordConfirm: 'Repeat the password',
    referralCode: 'Invitation code',
    referralCodePlaceholder: 'For example, 8K3M7QPL',
    referralCodeHint: 'Optional — use it if a 19CRT member invited you',
    referralCodeRequiredHint: 'Registration is by invitation only: use the link or code of the person who invited you',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} bonus ${pluralEn(signals, 'signal', 'signals')}` : null,
        promo === null ? null : `a ${promo}\u00A0USDT promo bonus`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `If a 19CRT member gave you this code, the invitation counts after a first deposit of at least ${deposit}\u00A0${asset}`
        : `If a 19CRT member gave you this code, you get ${perks.join(' and ')} after a first deposit of at least ${deposit}\u00A0${asset}`
    },
    forgot: 'Forgot your password?',
    createAccount: 'Create an account',
    haveAccount: 'Already have an account?',
    noAccount: 'No account yet?',
    backToSignIn: 'Back to sign in',
    verifyTitle: 'Confirm your email',
    verifySent: (email: string) => `We sent a link to ${email}`,
    verifySentAnywhere: 'We sent a link to your email address',
    verifyHint:
      'Open the link from the email to confirm the address: password recovery emails go there. If it has not arrived, check the spam folder.',
    verifyEmailPrompt: 'Enter the email the account is registered to',
    verifyResend: 'Send the email again',
    verifyResendIn: (seconds: Count) => `You can send it again in ${seconds.text}s`,
    verifyResent: 'Another email is on its way',
    verifyResentMaybe: 'If the address is registered and not yet confirmed, a new email is on its way',
    verifyChecking: 'Checking the link',
    verifyDone: 'Email confirmed',
    verifyDoneHint: 'Opening the app',
    verifyDoneSignInHint: 'Opening sign in',
    resetTitle: 'Reset password',
    resetSubtitle: 'Enter your email and we will send a link to change the password',
    resetSend: 'Send the link',
    resetSent: 'If that account exists, the email is on its way',
    resetSentHint:
      'We never reveal whether an address is registered\u00A0— otherwise this form could be used to probe other people’s emails. Check your inbox and the spam folder.',
    newPassword: 'New password',
    newPasswordSubtitle: 'Choose a new password. The old one stops working immediately.',
    newPasswordSubmit: 'Save password',
    requestNewLink: 'Request a new link',
    newPasswordDone: 'Password updated',
    newPasswordDoneHint: 'Opening sign in',
    signOut: 'Sign out',
    totpCode: 'Verification code',
    totpHint: 'Six digits from your authenticator app',
    recoveryCode: 'Recovery code',
    recoveryCodeHint: 'Eight characters from the codes you saved when you turned on 2FA',
    useRecoveryCode: 'Use a recovery code',
    useAppCode: 'Use the authenticator app',
    resetLinkInvalid: 'This link doesn’t work',
    errors: {
      invalidCredentials: 'Wrong email or password',
      emailTaken: 'That email is already registered',
      weakPassword: 'That password is too short',
      passwordRule: 'The password must contain letters and digits',
      passwordsDiffer: 'The passwords do not match',
      invalidEmail: 'Enter an address in the name@example.com format',
      invalidReferralCode: 'Check the invitation code',
      referralRequired: 'Enter your invitation code',
      emailRequired: 'Enter your email',
      passwordRequired: 'Enter your password',
      accountBlocked: 'This account is restricted. Please contact support',
      rateLimited: 'Too many attempts. Try again later',
      totpRequired: 'Enter the code from your authenticator app',
      totpInvalid: 'The code did not match. Check the time on your phone and try a new one',
      totpFormat: 'An app code is six digits, a recovery code is eight characters',
      expiredToken: 'That link has expired. Request a new one',
      verifyLinkUsed: 'That link has already been used. Sign in — or open the newest email if you requested another one',
      missingToken: 'That link is incomplete. Open it from the email in full',
      validation: 'Please check the fields you filled in',
      network: 'No connection to the server. Check your network',
    },
  },

  home: {
    promoLabel: 'Promotions',
    banners: [
      {
        title: 'Gold signals',
        subtitle: 'A code from our Telegram channel — a trade in one step',
        cta: 'Enter code',
      },
      {
        title: 'Markets in real time',
        subtitle: 'Live prices, charts and order books for every pair',
        cta: 'Explore markets',
      },
      {
        title: 'Fund your account in minutes',
        subtitle: 'Deposit USDT via TRC20 or ERC20',
        cta: 'Deposit',
      },
    ],
    pauseAnnouncements: 'Pause announcements',
    resumeAnnouncements: 'Resume announcements',
    announcementsLabel: 'Announcements',
    announcements: [
      'Check the asset and network before making a deposit',
      'Manage account protection in Security settings',
      'View your transaction history in Assets',
    ],
    totalBalance: 'Total balance',
    quickTrade: 'Quick trading',
    quickTradeHint: 'Spot and futures in one terminal',
    quickActions: 'Quick actions',
    signalLive: 'Signal available',
    signalNext: 'Next signal',
    signalClosesIn: 'closes in',
    signalOpensAt: (time: string) => `opens at ${time}`,
    signalsTitle: 'Gold signals',
    allSignals: 'All signals',
    marketMoves: 'Market moves',
    pair: 'Pair',
    lastPrice: 'Price',
    change24h: '24h change',
    /** After the figure in the home balance's change pill: « +1.30% 24h ». */
    change24hShort: '24h',
    volume24h: '24h volume',
    allMarkets: 'All markets',
    pricesUnavailable: 'Prices unavailable — the portfolio cannot be valued in full',
    ratesUnavailable: 'Rates are temporarily unavailable',
    marketsUnavailable: 'The market list is temporarily unavailable',
    signalsNone: 'No signals right now — new codes come out in our Telegram channel',
  },

  markets: {
    title: 'Markets',
    search: 'Search a pair',
    spot: 'Spot',
    futures: 'Futures',
    gainers: 'Gainers',
    losers: 'Losers',
    volume: '24h volume',
    volumeShort: 'vol',
    typeLabel: 'Market type',
    sortLabel: 'Sorting',
    favourites: 'Favourites',
    addFavourite: 'Add to favourites',
    removeFavourite: 'Remove from favourites',
    emptyFavourites: 'No favourites yet — star the pairs you watch',
    clearSearch: 'Clear the search',
    empty: 'No matches',
    emptyHint: 'Check the pair symbol — BTC or SOL, for example.',
    emptySpot: 'No spot pairs are open yet',
    emptyFutures: 'No futures pairs are open yet',
    loadFailed: 'Could not load the market list',
    pricesUnavailable: 'Quotes are temporarily unavailable. Prices will refresh on their own.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Gold' } as Record<string, string>,
  },

  wallet: {
    title: 'Wallet',
    totalValue: 'Total account value',
    accounts: 'My accounts',
    latestWithdrawal: 'Latest withdrawal',
    coin: 'Coin',
    searchCoin: 'Search a coin',
    quickActions: 'Quick actions',
    coins: 'Coins',
    hideBalance: 'Hide balance',
    showBalance: 'Show balance',
    loadError: 'Could not load your balance',
    pricesUnavailable: 'Prices unavailable — the portfolio cannot be valued in full',
    coinsEmpty: 'No coins yet. Make a deposit and they will show up here.',
    accountSpot: 'Spot account',
    accountSpotHint: 'Available to trade',
    accountFutures: 'Futures account',
    accountFuturesHint: 'Margin backing positions',
    accountCopy: 'Copy trading (archive)',
    accountCopyHint: 'Move it to spot',
    actions: {
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      transfer: 'Transfer',
      history: 'History',
    },
    deposit: {
      title: 'Deposit',
      history: 'Deposit history',
      chain: 'Network',
      saveQr: 'Save QR code',
      address: 'Deposit address',
      copyAddress: 'Copy address',
      notice: 'Important',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Send only ${asset} on ${network}. Coins sent on another network are lost.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Credited after ${count.text} network ${pluralEn(count, 'confirmation', 'confirmations')}, usually in about ${minutes.text} ${pluralEn(minutes, 'minute', 'minutes')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Minimum deposit is ${amount} ${asset}.`,
      waiting: 'Waiting for your transfer',
      incoming: 'Deposits on the way',
      incomingFirst: 'Seen on the network, waiting for the first confirmation',
      incomingProgress: (done: Count, total: Count) => `Confirmations: ${done.text} of ${total.text}`,
      selectAsset: 'Choose a coin',
      noAssets: 'Deposits are temporarily unavailable. Please try again later.',
      noNetworks: 'No network is available for this coin yet — pick another one.',
      addressFailed: 'Could not get a deposit address',
      noticePermanent: 'The address is permanent: use it for every future deposit.',
      qrSaved: 'QR code saved',
      qrFailed: 'Could not save the QR code',
      paymentLink: 'Wallet link',
      paymentLinkHint: 'Open it in your wallet — the network and the address are filled in for you.',
      copyLink: 'Copy link',
    },
    withdraw: {
      title: 'Withdraw',
      history: 'Withdrawal history',
      asset: 'Withdrawal asset',
      selectAsset: 'Select an asset',
      network: 'Withdrawal network',
      address: 'Withdrawal address',
      addressPlaceholder: (network: string) => `Enter ${/^[aeiou]/i.test(network) ? 'an' : 'a'} ${network} address`,
      addressPaste: 'Paste address',
      addressFromBook: (label: string): string => `Confirmed in your address book · ${label}`,
      paste: 'Paste',
      addressInvalid: (network: string) => `This does not look like a ${network} address`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `That is a ${detected} address, but ${selected} is selected`,
      amount: 'Amount',
      amountPlaceholder: '0.00',
      networkFee: 'Network fee',
      platformFee: (rate: string): string => `Platform fee (${rate}%)`,
      feeFree: 'free for now',
      feeDynamic: 'at the current gas price',
      willReceive: 'You receive',
      notice: 'Important',
      noticeReview: 'Every request is checked before it is sent. Funds are reserved immediately.',
      noticeTiming: 'Sent within 24 hours of approval.',
      noticeMinimum: (amount: string, asset: string) =>
        `Minimum withdrawal is ${amount} ${asset}.`,
      noticeSupport:
        'Check the address yourself: a transfer to the wrong one cannot be recovered. Support resolves disputes by request number.',
      insufficient: 'Not enough funds',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Left today: ${remaining} of ${limit} USDT.` : `Unverified accounts may withdraw up to ${limit} USDT a day; ${remaining} left. Verify your identity to raise it.`,
      addressNotConfirmed: 'Withdrawals go only to addresses confirmed in the address book',
      limitReached: (verified: boolean): string => (verified ? 'Daily withdrawal limit reached' : 'Daily limit for unverified accounts reached — verify your identity in the profile'),
      belowMinimum: (amount: string, asset: string) => `Minimum ${amount} ${asset}`,
      confirmTitle: 'Check the request',
      confirmSubtitle: 'A withdrawal cannot be cancelled once it is sent. Check the network and the address.',
      confirmAction: 'Confirm withdrawal',
      failed: 'The request could not be created',
      submitted: 'Request created',
      submittedHint: 'Once checked, the funds go out to the network. Follow the status in your withdrawal requests.',
      requestId: 'Request number',
      statusPending: 'Under review',
      toRequests: 'Go to withdrawal requests',
      empty: 'No assets are available for withdrawal right now',
      loadFailed: 'The asset list could not be loaded',
      routeUnavailable: 'Withdrawals on this network are unavailable right now — choose another network',
      invalidAmount: 'The amount does not work: check the precision and the network fee',
      edit: 'Edit the request',
    },
    withdrawals: {
      title: 'Withdrawal requests',
      toAddress: (address: string): string => `to ${address}`,
      note: 'Rejected and failed requests return the reserve to your account. Open a request to see its transaction or the reason.',
      empty: 'No withdrawal requests yet. Each request will show its status here, from review to network confirmation.',
      loadError: 'Could not load your requests',
      details: 'Withdrawal request',
      created: 'Created',
      transaction: 'Transaction',
      openExplorer: 'Open in explorer',
      reason: 'Reason',
      status: {
        pending: 'Under review',
        approved: 'Approved',
        rejected: 'Rejected',
        broadcasting: 'Sending',
        sent: 'Sent',
        confirmed: 'Confirmed',
        failed: 'Failed',
      },
      statusHint: {
        pending: 'The request is being checked. The amount is reserved on your account.',
        approved: 'Checked and waiting to be sent to the network.',
        rejected: 'The request was rejected; the reserve is back on your account.',
        broadcasting: 'The transaction is being sent to the network.',
        sent: 'The transaction is on the network, waiting for confirmation.',
        confirmed: 'The network confirmed the transaction. The funds have arrived.',
        failed: 'Sending failed; the reserve is back on your account.',
      },
    },
    transfer: {
      title: 'Transfer',
      from: 'From',
      fromAvailable: (amount: string): string => `From · ${amount} available`,
      to: 'To',
      submit: 'Transfer',
      swap: 'Swap the two accounts',
      selectFrom: 'Source account',
      selectTo: 'Destination account',
      selectAsset: 'Choose a coin',
      after: 'After the transfer',
      insufficient: 'Not enough funds in the source account',
      done: 'Transfer complete',
      hint: 'Transfers between your own accounts are instant and free.',
      empty: 'Nothing to transfer yet — make a deposit first.',
      failed: 'Could not complete the transfer. Try again',
    },
    history: {
      title: 'Transaction history',
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      trade: 'Trade',
      fee: 'Fee',
      transfer: 'Transfer',
      pnl: 'Result',
      empty: 'No transactions yet. Deposits, withdrawals and trades will appear here.',
      emptyFiltered: 'No transactions match this filter',
      loadError: 'Could not load your history',
      today: 'Today',
      yesterday: 'Yesterday',
      balanceAfter: (amount: string) => `balance ${amount}`,
      onlyAccount: (account: string) => `${account} only`,
      resetAccount: 'Show all accounts',
      accountFilter: 'Account',
      allAccounts: 'All accounts',
      withdrawalsLink: 'Withdrawal request statuses',
      badges: {
        credited: 'Credited',
        sent: 'Sent',
        fee: 'Fee',
        reserved: 'Reserved',
        returned: 'Returned',
        released: 'Released',
        filled: 'Filled',
        settled: 'Settled',
        liquidated: 'Liquidated',
        done: 'Done',
        adjusted: 'Adjusted',
        paid: 'Paid',
      },
      filters: {
        all: 'All',
        rewards: 'Rewards',
        deposits: 'Deposits',
        withdrawals: 'Withdrawals',
        trades: 'Trades',
        transfers: 'Transfers',
      },
      types: {
        deposit: 'Deposit',
        withdrawal: 'Withdrawal',
        withdrawal_fee: 'Withdrawal fee',
        withdrawal_platform_fee: 'Platform withdrawal fee',
        withdrawal_lock: 'Withdrawal reserved',
        withdrawal_release: 'Withdrawal reserve returned',
        order_lock: 'Reserved for a limit order',
        order_release: 'Order reserve returned',
        bonus: 'Promo bonus',
        trade_buy: 'Buy',
        trade_sell: 'Sell',
        trading_fee: 'Trading fee',
        realized_pnl: 'Trade result',
        signal_programmed_profit: 'Programmed signal profit',
        funding: 'Funding',
        margin_lock: 'Margin reserved',
        margin_release: 'Margin released',
        liquidation: 'Liquidation',
        transfer_in: 'Transfer in',
        transfer_out: 'Transfer out',
        copy_allocation: 'Allocated to copying',
        copy_release: 'Returned from copying',
        copy_commission: 'Copy trading fee',
        referral: 'Referral reward',
        adjustment: 'Adjustment',
      },
    },
  },

  trade: {
    title: 'Trade',
    menu: {
      account: 'Account',
      trading: 'Trading',
      more: 'More',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Buy',
    sell: 'Sell',
    marketOrder: 'Market',
    limitOrder: 'Limit',
    price: 'Price',
    marketPrice: 'At market',
    quantity: 'Quantity',
    leverage: 'Leverage',
    takeProfitStopLoss: 'Take profit / stop loss',
    positions: 'Positions',
    orders: 'Orders',
    history: 'History',
    copyTrading: 'Copy trading',
    unrealizedPnl: 'Unrealized P&L',
    unrealizedPnlShort: 'P&L',
    margin: 'Margin',
    entry: 'Entry',
    size: 'Size',
    liquidation: 'Liquidation',
    close: 'Close',
    orderBook: 'Order book',
    noPositions: 'Place an order above and the position will appear here.',
    balance: 'Balance',
    perpetual: 'Perpetual',
    perpShort: 'Perp',
    fundingIn: (time: string) => `in ${time}`,
    position: 'Position',
    closeAtMarket: 'Close at market',
    keepPosition: 'Keep the position',
    placeBuyOrder: 'Place buy order',
    placeSellOrder: 'Place sell order',
    placeLongOrder: 'Place long order',
    placeShortOrder: 'Place short order',
    isolatedMargin: 'Isolated margin',
    crossMargin: 'Cross margin',
    markShort: 'Mark',
    liqPrice: 'Liq. price',
    noPositionsTitle: 'No open positions',
    noOrdersTitle: 'No active orders',
    noHistoryTitle: 'No trades yet',
    pair: 'Pair',
    selectPair: 'Select a pair',
    sideLabel: 'Direction',
    orderType: 'Order type',
    sizeShare: 'Share of available balance',
    quantityShort: 'Qty',
    takeProfit: 'Take profit',
    stopLoss: 'Stop loss',
    tabsLabel: 'My trades',
    openChart: 'Chart',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `Buy ${symbol}`,
    sellAsset: (symbol: string) => `Sell ${symbol}`,
    enterQuantity: 'Enter a quantity',
    insufficient: 'Not enough funds on this account',
    belowMinNotional: (amount: string, asset: string) =>
      `Minimum order size is ${amount} ${asset}`,
    bookEmpty: 'No resting orders yet',
    bookUnavailable: 'Order book unavailable',
    tradingUnavailable:
      'The trading service is not responding, so orders cannot be placed right now. Balances, deposits and withdrawals are unaffected.',
    marketsFailed: 'Could not load the pair list',
    noMarkets: 'No trading pairs yet — they appear as markets go live.',
    confirmTitle: 'Check the order',
    confirmSubtitle: 'A filled order cannot be undone.',
    marginRequired: 'Margin used',
    cost: 'Total',
    liquidationEstimate: 'Estimated liquidation',
    submitted: 'Order placed',
    submitFailed: 'Could not place the order',
    futuresSoon: 'Futures are temporarily unavailable. Spot trading works.',
    marginMode: 'Margin',
    cross: 'Cross',
    isolated: 'Isolated',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cross: the whole futures balance backs the position'
        : 'Isolated: only this position’s margin is at risk',
    reduceOnly: 'Reduce only',
    futuresAccount: 'Futures account',
    equity: 'Equity',
    marginUsed: 'In margin',
    marginRatio: 'Risk level',
    funding: 'Funding',
    fundingRate: '8h rate',
    nextFunding: 'Next',
    fundingPaid: 'Funding',
    protection: 'Protection',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take profit and stop loss',
    protectionSheetSubtitle: 'The engine closes at market the moment the price touches a level.',
    clearLevel: 'No level',
    addMargin: 'Add margin',
    removeMargin: 'Remove margin',
    marginDelta: 'Margin amount',
    marginDeltaHint: 'Isolated position: more margin moves liquidation further away',
    addMarginKyc: 'Adding margin opens once your documents are approved. You can already remove margin, change take profit and stop loss, or close the position.',
    saveProtection: 'Save',
    protectionSaved: 'Protection updated',
    protectionFailed: 'Could not update the position',
    protectionInvalid: 'The level is on the wrong side of the entry price',
    leverageTooHigh: 'Leverage exceeds the market maximum',
    leverageTier: (cap: number, notional: string) => `Up to ${cap}× for a ${notional} USDT position`,
    futuresDisabled: 'Futures orders are not accepted right now',
    liquidated: 'Liquidated',
    restingToast: (symbol: string, price: string) => `Limit order ${symbol} at ${price} is resting and waiting for the price`,
    cancelledToast: 'Order cancelled, funds released',
    costSell: 'You receive',
    costBasis: 'Cost',
    enterProtection: 'The take profit or stop loss level is not a valid price',
    leverageLocked: (leverage: number) => `Leverage is fixed at ${leverage}× by the open position. Close it to change it`,
    marginReleased: 'Margin released',
    realizedEstimate: 'Estimated result',
    addsPosition: (quantity: string, asset: string) => `Adds ${quantity} ${asset} to the open position`,
    reducesPosition: (quantity: string, asset: string) => `Reduces the open position by ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Closes the open ${quantity} ${asset} position`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Closes the ${closeQuantity} ${asset} position and opens ${openQuantity} ${asset} the other way`,
    byAccount: 'whole account',
    cancelledToastPlain: 'Order cancelled',
    fillBuy: 'Buy',
    fillSell: 'Sell',
    marginFloor: (floor: string) => `Margin cannot go below the initial margin of ${floor} USDT`,
    marginRemovable: (amount: string) => `Up to ${amount} USDT can be removed`,
    enterPrice: 'Enter the limit price',
    markPrice: 'Mark price',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'The price is stale — the quote feed is behind. Try again in a few seconds.',
    marketDisabled: 'Trading in this pair is paused',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `${side} ${quantity} ${asset} at ${price} — filled`,
    closedToast: (symbol: string, pnl: string) => `${symbol} closed, result ${pnl} USDT`,
    positionsFailed: 'Could not load positions',
    noOrders: 'A limit order waits here until it fills.',
    ordersFailed: 'Could not load orders',
    noHistory: 'Your first filled trade will show up in this list.',
    historyFailed: 'Could not load trade history',
    cancelOrder: 'Cancel',
    cancelFailed: 'Could not cancel the order',
    closeFailed: 'Could not close the position',
    closeConfirmTitle: 'Close the position?',
    closeConfirmSubtitle:
      'The position is closed at the market price and the result becomes final. This cannot be undone.',
  },

  chart: {
    title: 'Chart',
    interval: 'Interval',
    chartType: 'Chart type',
    candles: 'Candles',
    line: 'Line',
    high24h: '24h high',
    low24h: '24h low',
    volume24h: '24h volume',
    empty: 'The chart will appear once this pair has data',
    failed: 'Could not load the chart',
    unknownPair: 'No such pair — pick one on the Markets tab',
    toMarkets: 'To markets',
  },

  security: {
    title: 'Security',
    subtitle: 'A second factor protects sign-in and withdrawals',
    statusOn: 'On',
    statusOff: 'Off',
    explain:
      'The code from your authenticator app is asked at sign-in and for every withdrawal request. Without it the account is protected by the password alone.',
    setup: 'Set up an authenticator',
    step1: 'Scan the code in Google Authenticator, 1Password or Aegis',
    secretLabel: 'Or enter the secret manually',
    step2: 'Enter the code from the app',
    enable: 'Turn on',
    enabled: 'Two-factor is on',
    disableTitle: 'Turn two-factor off',
    disableHint: 'Enter the current code to prove the phone is yours',
    disable: 'Turn off',
    disabled: 'Two-factor is off',
    codeInvalid: 'The code did not match. Check the time on your phone and try a new one',
    failed: 'Could not change the security settings',
    withdrawalCode: 'Verification code',
    withdrawalCodeHint: 'Withdrawals are protected by your second factor: enter the code from the app',
    recoveryTitle: 'Recovery codes',
    recoveryIntro: 'Keep these codes somewhere safe. Each one opens a sign-in or a withdrawal once when your phone is out of reach. They are not shown again.',
    recoveryCopy: 'Copy codes',
    recoveryCopied: 'Codes copied',
    recoverySaved: 'I have saved the codes',
    recoveryLeft: (count: number): string => `Recovery codes left: ${count}`,
    recoveryRegenerate: 'New recovery codes',
    recoveryRegenerateHint: 'The old codes stop working. Enter a code from the app',
    recoveryRegenerated: 'New recovery codes issued',
    codeOrRecovery: 'Six digits from the app or a recovery code',
  },

  addressBook: {
    title: 'Withdrawal addresses',
    hint: 'A new address starts working after you open the link from the email, so a stolen session cannot send funds to its own address.',
    add: 'Add an address',
    address: 'Address',
    label: 'Label',
    labelPlaceholder: 'For example, Ledger',
    send: 'Send the confirmation email',
    saved: 'Saved addresses',
    empty: 'No addresses yet',
    emptyBody: 'Add the address you withdraw to: once it is confirmed from the email, it is one tap away in the withdrawal form.',
    statusConfirmed: 'confirmed',
    statusPending: 'waiting for email confirmation',
    resend: 'Resend',
    resendLink: 'Resend the confirmation email',
    remove: 'Remove address',
    removeTitle: 'Delete this address?',
    removeHint: 'Withdrawals to it will stop until you add and confirm it again.',
    linkSent: 'Email sent — open the link to confirm the address',
    alreadyConfirmed: 'This address is already confirmed',
    invalid: 'The address does not fit the chosen network',
    confirmed: (short: string): string => `Address ${short} confirmed`,
    confirmFailed: 'The link is invalid or has expired',
  },

  sessions: {
    title: 'Sessions and devices',
    subtitle: 'Where your account is signed in',
    current: 'This device',
    started: 'Signed in',
    lastSeen: 'Active',
    end: 'End',
    endOthers: 'End all other sessions',
    ended: 'Session ended',
    endedOthers: (count: number): string => `Sessions ended: ${count}`,
    empty: 'No other active sessions',
    failed: 'Could not load sessions',
    hint: 'An ended session loses access within fifteen minutes: that is how long an issued access key lives.',
  },

  profile: {
    title: 'Profile',
    accountId: 'Account ID',
    idCopied: 'ID copied',
    loadFailed: 'Could not load your profile',
    partnerProgram: 'Partner programme',
    partnerProgramHint: 'Team, level and rewards',

    verification: 'Verification',
    verificationNone: 'Not started',
    verificationPending: 'In review',
    verificationInReview: 'In review',
    verificationApproved: 'Verified',
    verificationRejected: 'Rejected',
    verificationResubmit: 'New documents needed',
    verificationCta: 'Start',
    verificationCtaAgain: 'Submit again',
    verificationPrompt: 'Upload your passport — takes two minutes',
    verificationPendingHint: 'An operator usually reviews documents within a day.',
    verificationRejectedHint: 'The operator did not accept the documents. You can apply again.',
    verificationOpen: 'Open the application',

    withdrawalAddressesHint: 'Address book, confirmed by email',
    withdrawalAddresses: 'Withdrawal addresses',
    addressesSubtitle: 'Addresses you have already withdrawn to',
    addressesEmpty: 'You have not withdrawn yet. The address from your first request appears here.',
    addressesFailed: 'Could not load the addresses',
    addressCopied: 'Address copied',

    security: 'Security',
    accountAccess: 'Email and password',
    sessions: 'Sessions and devices',
    sessionsHint: 'Where you’re signed in',
    securityOn: 'Two-factor is on',
    securityOff: 'Set up two-factor',
    securitySubtitle: 'Account access',
    securityEmailVerified: 'Email confirmed',
    securityEmailUnverified: 'Email not confirmed',
    securityPassword: 'Change password',
    securityPasswordHint: 'We will email you a link to set a new one',

    notifications: 'Notifications',
    pushOn: 'Push on',
    pushOff: 'Push off',
    pushToggle: 'Push notifications',
    pushUnsupported: 'This browser cannot do push notifications',
    pushDenied: 'Notifications are blocked in your browser settings',
    pushUnavailable: 'Push notifications are not set up on the server yet',

    pushFailed: 'Could not turn push notifications on',

    theme: 'Theme',
    themeHint: 'Black is the brand and saves battery on OLED. Light is for bright sun. “System” follows your phone.',
    themeNames: { system: 'System', dark: 'Dark', light: 'Light' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Follows the device setting',
    language: 'Language',
    languageHint: 'Interface language. Amounts keep one format; dates follow the language.',

    support: 'Support',
    supportTitle: 'Contact support',
    supportChoose: 'Choose how to reach us',
    supportVia: 'Telegram or e-mail',
    supportTelegram: 'Message on Telegram',
    supportEmail: 'Send an e-mail',
    about: 'About',
    version: 'Version',

    signOut: 'Sign out',
    signOutTitle: 'Sign out of this account?',
    signOutBody:
      'Balances, open positions and your signal history stay exactly as they are — you come back to them at the next sign-in. Other devices stay signed in.',

    kyc: {
      title: 'Verification',
      intro:
        'Only the reviewing operator sees this. The document number is stored encrypted and is never shown in full.',
      loadFailed: 'Could not load the application',

      document: 'Document',
      documentType: 'Document type',
      typePassport: 'Passport',
      typeIdCard: 'ID card',
      typeDriverLicense: 'Driver licence',
      number: 'Document number',
      numberPlaceholder: '4510 123456',
      numberHint: 'Series and number, exactly as printed',
      country: 'Issuing country',
      countryPlaceholder: 'Choose a country',
      issueDate: 'Issued on',
      expiryDate: 'Valid until',

      personal: 'Document holder',
      lastName: 'Surname',
      firstName: 'First name',
      dateOfBirth: 'Date of birth',

      documents: 'Document photo',
      docMain: 'Main page',
      docMainHint: 'The spread with the photo and the number',
      docFront: 'Front side',
      docFrontHint: 'The side with the photo and the number',
      choose: 'Choose a file',
      replace: 'Replace',
      remove: 'Remove',
      fileHint: (size: string) => `JPG, PNG or PDF, up to ${size} MB`,
      megabytes: 'MB',

      submit: 'Submit for review',
      submitFailed: 'Could not submit the application. Check the fields and try again',
      uploadFailed:
        'The details went through, the files did not. Try attaching them once more.',
      uploadRetry: 'Upload documents',
      submitted: 'Application submitted',
      submittedHint: 'An operator will check the documents and update the status.',

      review: 'Application in review',
      reviewHint: 'The details cannot be changed while the review is running.',
      approved: 'Verification complete',
      approvedHint: 'An operator confirmed the documents.',
      rejected: 'Application rejected',
      rejectedHint: 'Correct the details and submit the application again.',
      resubmit: 'New documents needed',
      resubmitHint: 'Upload a valid document and submit the application again.',
      rejectionReason: 'Reason',
      submittedAt: (date: string) => `Submitted ${date}`,
      attached: 'Attached documents',
      attachedEmpty: 'No file is attached to this application',
      startNew: 'Submit again',
      step: (current: number, total: number): string => `Step ${current} of ${total}`,

      gate: {

        title: 'Verify your identity to use your funds',

        body: 'You can deposit right away. Trading, signals, transfers and withdrawals open once your documents are approved — usually within a day.',

        pending: 'Your documents are under review — access opens once they are approved, usually within a day.',

        action: 'Verify identity',

      },

      kinds: {
        passport_main: 'Main page',
        passport_registration: 'Registration page',
        selfie_with_document: 'Selfie with the document',
        proof_of_address: 'Proof of address',
      },

      errors: {
        required: 'Fill this in',
        number: 'Letters, digits, spaces and hyphens only',
        name: 'Letters, spaces, apostrophes and hyphens only',
        country: 'Choose a country',
        date: 'Pick a date',
        issueFuture: 'The issue date cannot be in the future',
        expiryPast: 'The document has expired',
        expiryBeforeIssue: 'The expiry date must come after the issue date',
        age: 'Verification is available from 18',
        birthAfterIssue: 'The date of birth must precede the issue date',
        fileType: 'JPG, PNG or PDF only',
        fileSize: (size: string) => `The file is larger than ${size} MB`,
        documentsRequired: 'Attach a photo or scan of the document',
        fileMissing: 'Add this file',
      },
    },
  },

  countries: {
    search: 'Country name or code',
    popular: 'Popular',
    all: 'All countries',
    nothing: 'Nothing found. Try the English name or a code such as DE',
    found: (count: number): string => `Countries found: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Promo bonus of ${amount} USDT unlocked and credited to spot`,
    promoExpired: 'The promo bonus expired: its conditions were not met in time',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Your invitee ${name} qualified: +${signals.text} bonus ${pluralEn(signals, 'signal', 'signals')}`
        : `Your invitee ${name} qualified`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} bonus ${pluralEn(signals, 'signal', 'signals')}` : null,
        promo === null ? null : `a ${promo} USDT promo bonus locked until the condition is met`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Your referral qualification is complete' : `Qualification complete: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Qualification revoked: a withdrawal was requested inside the hold period. The bonus signals and the promo bonus are withdrawn'
          : 'Qualification revoked: the bonus signals and the promo bonus are withdrawn'
        : `Your invitee ${name}’s qualification was revoked: the bonus signals earned for them are withdrawn`,
    referralRewardPaid: (amount: string): string => `${amount} USDT paid under the partner programme`,
    referralRewardRejected: (amount: string): string => `A ${amount} USDT reward was rejected by the operator`,
    partnerLevelReached: (level: string, amount: string): string =>
      `${level} reached: a ${amount} USDT bonus is queued for payout`,
    title: 'Notifications',
    settingsTitle: 'Notification settings',
    settingsHint: 'A muted category stays in history but never asks for attention: no badge, no push. Push works when it is on in the profile.',
    channelInApp: 'In app',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'What the bot sends to your chat. A category switched off here stays out of Telegram but remains in the app.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram connected' : `Telegram connected: ${name}`),
    telegramLinkedHint: 'If this wasn’t you, tap to disconnect it.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram disconnected' : `Telegram disconnected: ${name}`),
    telegramUnlinkedHint: 'This chat was connected to another account.',
    categories: { deposit: 'Deposits', withdrawal: 'Withdrawals', copy: 'Copy trading', trade: 'Trades and positions', signal: 'Signals', promo: 'Promo bonuses', referral: 'Partner programme', kyc: 'Verification', system: 'System' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Mark all read',
    markAllFailed: 'Could not mark the notifications as read',
    loadFailed: 'Could not load notifications',
    empty:
      'No notifications yet. Credited deposits, answers on withdrawal requests, signals and trade results will show up here.',
    unread: 'Unread',
    today: 'Today',
    yesterday: 'Yesterday',
    depositCredited: (amount: string, asset: string) => `${amount} ${asset} credited`,
    withdrawalApproved: 'Withdrawal approved',
    withdrawalRejected: 'Withdrawal rejected',
    withdrawalSent: 'Withdrawal sent on-chain',
    marginCall: 'Margin call',
    positionLiquidated: 'Position liquidated',
    copyOpened: (master: string) => `${master} opened a position`,
    copyClosed: (master: string) => `${master} closed a position`,
    copyStopLoss: 'Subscription stop-loss triggered: positions closed, funds returned to the spot account',
    copyMasterStopped: 'Trader stopped: copying ended, funds returned to the spot account',
    copyReleased: (amount: string) => `Returned to spot: ${amount}\u00A0USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Profit share to the trader: ${commission} USDT on ${profit} USDT of profit` : `Profit share to ${master}: ${commission} USDT on ${profit} USDT of profit`,
    signalLive: (title: string, until: string) => `Signal “${title}” is open until ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `${symbol} position liquidated at ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: take profit hit at ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: stop loss hit at ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Limit order ${symbol} filled: ${quantity} at ${price}`,
    orderRejected: (symbol: string) => `Limit order ${symbol} was not filled: the engine rejected the trade`,
    orderRejectedFunds: (symbol: string) => `Limit order ${symbol} was not filled: not enough margin on the futures account`,
    orderRejectedLeverage: (symbol: string) => `Limit order ${symbol} was not filled: leverage exceeds the limit for that size`,
    kycApproved: 'Verification approved',
    kycRejected: 'Verification rejected',
    kycResubmit: 'Verification needs new documents',
    kycExpiring: (date: string) => `Your document expires on ${date} — renew your verification`,
    kycExpired: 'Your document has expired — upload a valid one',
    signalClosed: (title: string) => `Signal closed: ${title}`,
    systemAlert: (check: string) => `Incident: ${check}`,
    systemRecovered: (check: string) => `Recovered: ${check}`,
    heads: {
      signalLive: 'Signal opened',
      signalClosed: 'Signal closed',
      orderFilled: 'Limit order filled',
      orderRejected: 'Limit order not filled',
      takeProfit: 'Take profit hit',
      stopLoss: 'Stop loss hit',
      promoUnlocked: 'Promo bonus unlocked',
      promoExpired: 'Promo bonus expired',
      referralQualified: 'Invitee qualified',
      referralWelcome: 'Qualification complete',
      referralRevoked: 'Qualification revoked',
      rewardPaid: 'Reward paid',
      rewardRejected: 'Reward rejected',
      partnerLevelReached: 'New partner level',
      kycExpiring: 'Document expiring soon',
      kycExpired: 'Document expired',
      systemAlert: 'Incident',
      systemRecovered: 'Recovered',
    },
    fallback: {
      deposit: 'Deposit credited',
      withdrawal: 'Withdrawal request updated',
      trade: 'Position event',
      copy: 'Copy trading event',
      referral: 'Partner programme event',
      system: 'System notification',
    },
  },

  referrals: {
    title: 'Partner programme',
    rewardKind: {
      fee_rebate: 'Fee share',
      signal_grant: 'Bonus signals',
      promotional_credit: 'Promotional credit',
      deposit_bonus: 'Deposit share',
      welcome_bonus: 'Welcome share',
      level_bonus: 'Level bonus',
    },
    rewardStatus: {
      pending_review: 'Under review',
      approved: 'Approved',
      paid: 'Paid',
      rejected: 'Rejected',
      reversed: 'Reversed',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Locked',
      unlocked: 'Credited',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Member',
      bronze: 'Bronze',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
      ambassador: 'Ambassador',
    } as Record<string, string>,
    signalUnit: 'signals',
    membersFrom: (count: Count): string => `from ${count.text} ${pluralEn(count, 'member', 'members')}`,
    baseLevel: 'starting level',
    depositFrom: (amount: string): string => `deposit from ${amount}`,
    noWeeklySignals: 'no signals',
    perWeek: (count: Count): string => `${count.text} ${pluralEn(count, 'signal', 'signals')}/wk`,
    current: 'current',
    copyFailed: 'Could not copy',
    inviteText: 'Join 19CRT with my referral code',
    linkCopied: 'Link copied',
    codeCopied: 'Code copied',
    loadError: 'Could not load the partner programme',
    loadErrorHint: 'Check your connection and try again',
    retry: 'Try again',
    disabled: 'The partner programme is temporarily paused. Your stats and rewards are safe.',
    yourCode: 'Your code',
    codeHint: 'Invite directly — all terms are visible before registration',
    openQr: 'Open referral QR code',
    qrCode: 'QR code',
    share: 'Share',
    copy: 'Copy',
    yourLevel: 'Your level',
    participant: 'Member',
    allLevels: 'All levels',
    progressLabel: 'Progress to the next level',
    qualified: (): string => 'qualified',
    nextLevel: (remaining: Count, level: string): string => `another ${remaining.text} to ${level}`,
    maxLevel: 'highest level reached',
    invited: 'Invited',
    confirmed: 'Qualified',
    paidUsdt: 'Paid, USDT',
    signalsReceived: 'Signals received',
    transparentTitle: 'Transparent rewards',
    transparentHint:
      'Rewards are based only on trading fees actually collected. Your invitee’s deposit is never redistributed.',
    rewards: 'Rewards',
    rewardHistory: 'Reward history',
    underReview: 'Under review',
    emptyRewardsTitle: 'No rewards yet',
    emptyRewards: 'Rewards will appear after your team’s first qualifying activity.',
    levelsRules: 'Levels and rules',
    levelsRulesHint: 'Team, fee share and signals',
    inviteTitle: 'Invite to 19CRT',
    inviteSubtitle: 'The code is linked during registration',
    copyLink: 'Copy link',
    levelsTitle: 'Partner levels',
    levelsSubtitle: 'Only qualified direct members count',
    loadLevelsError: 'Could not load levels',
    levelTeam: 'Level and team',
    feeSignals: 'Fee share · signals',
    depositTiersTitle: 'Rates by deposit size',
    depositTiersHint: 'The larger the first deposit, the larger the share for both sides',
    tierDeposit: 'Deposit',
    tierReferrer: 'You',
    tierWelcome: 'Newcomer',
    tierFrom: (amount: string): string => `${amount} and up`,
    levelBonus: 'Level bonus',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Programme rules',
      qualification: (amount: string, asset: string): string =>
        `Qualification: a first credited deposit of at least ${amount} ${asset} in one transaction.`,
      qualificationKyc: 'Identity verification is required.',
      welcome: (signals: Count, days: Count): string =>
        `The newcomer gets ${signals.text} bonus ${pluralEn(signals, 'signal', 'signals')} valid for ${days.text} ${pluralEn(days, 'day', 'days')}.`,
      referrer: (signals: Count, days: Count): string =>
        `The partner gets ${signals.text} bonus ${pluralEn(signals, 'signal', 'signals')} valid for ${days.text} ${pluralEn(days, 'day', 'days')} for every qualified invitee.`,
      promo: (amount: string, required: string, days: Count): string =>
        `A ${amount} USDT promo bonus is locked and becomes spot money once the newcomer’s traded turnover reaches ${required} USDT within ${days.text} ${pluralEn(days, 'day', 'days')}; otherwise it expires.`,
      hold: (days: Count): string =>
        `Any withdrawal request within ${days.text} ${pluralEn(days, 'day', 'days')} of qualification cancels it together with the bonus signals and the promo bonus.`,
      feeShare:
        'The level’s fee share is calculated only from trading fees actually collected from the invitee. Money rewards are reviewed by the operator and paid separately — they are never credited to the balance.',
      secondLevel: (rate: string): string => `The partner’s own partner receives ${rate} of the same fee.`,
      depositTiers:
        'The share for the inviter and for the newcomer depends on the size of the first deposit — see the table in this sheet.',
      balanceShare: (percent: string): string =>
        `The deposit must also be at least ${percent}% of the inviter’s balance when it is credited.`,
      topup: (days: Count): string =>
        `A further deposit within ${days.text} ${pluralEn(days, 'day', 'days')} of the first is added to it, the pair is re-priced from the table and the difference is paid once.`,
      premiumSignal: (count: string): string =>
        `Permanent premium signal access opens once you have ${count} or more qualified invitees.`,
      antiabuse:
        'A device or address shared with the partner or their team sends the qualification to the operator for review.',
      paused: 'The programme is paused by the operator: no new qualifications or rewards are created.',
    },
    disclaimer:
      'Partner rewards are not guaranteed income. Registration, deposits and trading volume do not by themselves create a cash payout.',
    team: 'Team',
    teamStatus: {
      pending: 'awaiting a deposit',
      flagged: 'under operator review',
      qualified: 'qualified',
      revoked: 'qualification revoked',
    },
    joined: (date: string): string => `since ${date}`,
    showAll: (count: Count): string => `Show all (${count.text})`,
    showLess: 'Show less',
    promoTitle: 'Promo bonus',
    promoLocked: (amount: string): string => `${amount} USDT locked`,
    promoUnlocked: (amount: string): string => `${amount} USDT credited to spot`,
    promoExpired: 'Expired, the bonus is gone',
    promoRevoked: 'Bonus revoked',
    promoCondition: (traded: string, required: string): string => `Turnover ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `until ${date}`,
    promoHint: 'The bonus becomes money once your traded turnover reaches the condition before the deadline.',
  },

  signals: {
    title: 'Signals',
    tabs: { today: 'Today', history: 'History' },
    tabsLabel: 'Signal sections',
    access: { public: 'Public', base: 'Base', premium: 'Premium', bonus: 'Bonus', agent: 'Partner' },
    status: { scheduled: 'Scheduled', available: 'Available', executed: 'Executed', expired: 'Expired', cancelled: 'Cancelled' },
    side: { buy: 'Buy', sell: 'Sell' },
    direction: { buy: 'Long · buy', sell: 'Short · sell' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'local time',
    localClock: 'Local time',
    timeZone: (offset: string) => `Time zone ${offset}`,
    reason: {
      unavailable: 'This signal is not available right now',
      already: 'You have already executed this signal',
      expired: 'The execution window has expired',
      notReleased: 'This signal has not opened yet',
      cancelled: 'This signal was cancelled',
      balance: 'Insufficient funds in your Spot account',
      balanceFutures: 'Not enough USDT on your spot and futures accounts combined',
      positionConflict: 'You have an open position on this market in the opposite direction — close it to execute the signal',
      price: 'The price for this market is unavailable right now — try again in a few seconds',
      access: 'A different access level is required for this signal',
      kyc: 'Complete verification first — once your documents are approved, you can execute the signal',
      minimum: 'This signal takes a share of your total balance, and that share is below its minimum. Top up your balance to execute it',
      maximum: 'The amount is above this signal’s maximum',
      belowMinimum: (amount: string) => `This signal takes a share of your total balance, and that share is below its minimum of ${amount}\u00A0USDT. Top up your balance to execute it`,
      aboveMaximum: (amount: string) => `The amount is above this signal’s maximum — at most ${amount}\u00A0USDT`,
      notFound: 'No signal was found for this code',
      generic: 'The action could not be completed. Check the terms and try again',
      network: 'Could not reach the server. Check your connection and try again',
    },
    enterCode: 'Enter code',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Enter the code from the channel to see direction, size and terms before you confirm', executed: 'The result and actual parameters are in History', scheduled: (time: string) => `The code will appear in the channel at ${time}` },
    executing: 'Executing',
    notExecuted: 'Not executed',
    executedLabel: 'Executed',
    loadSignalsError: 'Could not load signals',
    noSignalsToday: 'No signals today',
    loadErrorHint: 'Check your connection and try again',
    retry: 'Try again',
    signalAvailable: 'Signal available',
    nextSignal: 'Next signal',
    windowOpenUntil: (time: string) => `Window open until ${time}`,
    opensAt: (time: string) => `Opens at ${time}`,
    execute: 'Execute',
    doneToday: 'All done for today',
    newBySchedule: 'New signals will appear on schedule',
    codeOverline: 'Signal code',
    checkTerms: 'Review terms',
    codeHint: 'Each code is published in our Telegram channel — enter it as soon as you see it.',
    openChannel: 'Open channel',
    channelLink: 'Telegram channel',
    panelTitle: 'Trade code',
    dealsTitle: 'Trades by code',
    dealsEmpty: 'No trades by code yet',
    dealsEmptyHint: 'Enter a code from the channel — the trade and its result will appear here',
    codePlaceholder: 'For example, 1QF9ET7RQ',
    codeRequired: 'Enter the code from the channel',
    codeLabel: 'Signal code',
    check: 'Review',
    schedule: 'Schedule',
    todaySignals: 'Today’s signals',
    noSignals: 'No signals right now. New codes come out on schedule in our Telegram channel.',
    riskDisclosure:
      'A signal is a trading idea, not a guaranteed result. Before you confirm, you will see the direction, amount, leverage, fee and exit terms.',
    loadHistoryError: 'Could not load signal history',
    historyEmpty: 'No history yet',
    historyEmptyHint: 'Executed signals and their actual parameters will appear here.',
    toSignals: 'View signals',
    allExecutions: 'All executions',
    signalAccepted: 'Signal accepted',
    confirmation: 'Confirm signal',
    sentToTrading: 'The order was sent to the trading system',
    checkBeforeExecute: 'Review the terms before execution',
    acceptedHint: 'Tapping again will not create a second order.',
    ready: 'Done',
    account: 'Account',
    spot: 'Spot',
    futures: 'Futures',
    perpetualMarket: 'futures',
    validUntil: 'Validity',
    signalName: 'Signal',
    until: (time: string) => `until ${time}`,
    timeLeft: (countdown: string) => `${countdown} left`,
    priceGuide: 'Indicative price',
    entryGuide: 'Indicative entry',
    marketPrice: 'Market',
    stopLoss: 'Stop loss',
    takeProfit: 'Take profit',
    autoClose: 'Auto-close',
    inDuration: (duration: string) => `in ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0d` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Already executed',
    executionUnavailable: 'Execution unavailable',
    executionAmount: 'Execution amount',
    marginAmount: 'Margin',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} of your total balance · ${total}\u00A0USDT`,
    estimatedFee: 'Estimated fee',
    totalDebit: 'Total debit',
    positionSize: 'Position size',
    willSell: 'To be sold',
    fundingNote: (amount: string) =>
      `${amount}\u00A0USDT will move from your spot account to your futures account for this trade. When the position closes, that amount, adjusted for the result, returns to your spot account.`,
    executionRisk:
      'A signal is a trading idea, not a guaranteed result. The execution price may differ from the estimate, and the trade can lose money. Only confirm a risk you understand.',
    executionRiskFutures:
      'A signal is a trading idea, not a guaranteed result. The entry price may differ from the estimate, and leverage magnifies both profit and loss, up to liquidation of the position. Only confirm a risk you understand.',
    retryExecution: 'Retry execution',
    outcomeLabel: 'result, USDT',
    outcomeOpen: 'open',
    outcome: { take_profit: 'closed at target', stop_loss: 'closed at stop', time: 'closed at expiry', manual: 'closed manually', liquidation: 'closed by liquidation' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'At target', stop_loss: 'At stop', time: 'At expiry', manual: 'Manual', liquidation: 'Liquidated' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'Open',
    executeFor: 'Execute up to',
    openLong: (amount: string) => `Open long · ${amount}\u00A0USDT`,
    openShort: (amount: string) => `Open short · ${amount}\u00A0USDT`,
    submittedToast: 'Signal submitted for execution',
    filledOf: (requested: string, filled: string) =>
      `Requested ${requested}\u00A0USDT, filled ${filled}\u00A0USDT — the quantity was rounded to the lot step.`,
    marginRounded: (requested: string) => `Requested ${requested}\u00A0USDT — the quantity was rounded to the lot step.`,
    longOpened: (margin: string, leverage: number) => `Long opened: ${margin}\u00A0USDT margin at ${leverage}× leverage.`,
    shortOpened: (margin: string, leverage: number) => `Short opened: ${margin}\u00A0USDT margin at ${leverage}× leverage.`,
    fundedFromSpot: (amount: string) => `${amount}\u00A0USDT moved from your spot account.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'take profit' : null, stopLoss ? 'stop loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Closes automatically in ${duration}`
      const byLevel = `at ${levels.join(duration === null ? ' or ' : ', ')}`
      return duration === null ? `Closes ${byLevel}` : `Closes ${byLevel} or in ${duration}`
    },
    openPosition: 'Go to position',
    returnedToSpot: (amount: string) => `Returned to spot: ${amount}\u00A0USDT`,
    positionAccepted: 'The position is open on your futures account',
  },

  notFound: {
    title: 'Page not found',
    message: 'There is no such page here — the link is out of date or the address has a typo.',
    action: 'Go to home',
  },
}
